import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { gqlClient } from '@/api';
import { discardUpload, uploadFile } from './uploads';

/**
 * The picture a link unfurls with, for an app, a post or the site: the
 * same swap for all three. Outside the Save button, like an icon or a
 * cover — the bytes go up in a request of their own and the row points at
 * what comes back, so holding the change in a draft until Save would leave
 * stored bytes nothing references every time a save failed.
 */

/**
 * Uploads `file` when there is one, runs the mutation that points the row
 * at it, and discards the upload if the mutation refuses — the bytes are
 * already stored when that happens, and nothing points at them.
 */
export const swapFile = async <R>(
  file: File | null,
  point: (fileId: string | null) => Promise<R>,
): Promise<R> => {
  const stored = file ? await uploadFile(file) : null;
  try {
    return await point(stored?.id ?? null);
  } catch (error) {
    if (stored) await discardUpload(stored.id);
    throw error;
  }
};

/**
 * One share picture, wherever it hangs: `mutation` names the GraphQL
 * mutation and its arguments, `queryKey` the cached record to write the
 * new address into.
 */
export const useShareImage = <
  T extends { shareImageUrl: string | null; updatedAt: string },
>(options: {
  mutation: string;
  variables: Record<string, unknown>;
  queryKey: unknown[];
}) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File | null) =>
      swapFile(file, async (fileId) => {
        const data = await gqlClient.request<
          Record<string, { shareImageUrl: string | null; updatedAt: string }>
        >(options.mutation, { ...options.variables, fileId });
        const [answer] = Object.values(data);
        return { shareImageUrl: answer.shareImageUrl, updatedAt: answer.updatedAt };
      }),
    onSuccess: ({ shareImageUrl, updatedAt }) => {
      // `updatedAt` too: the preview's address carries it, so the website
      // draws afresh rather than serving the hour-old picture.
      client.setQueryData<T>(options.queryKey, (current) =>
        current ? { ...current, shareImageUrl, updatedAt } : current,
      );
    },
  });
};

/**
 * Where the website is, for the previews.
 *
 * Not a query of its own. The record queries that feed the editors ask for
 * `websiteUrl` beside the record and put it here; a request of its own would
 * be one more round trip per screen for a value that never changes, and the
 * editors' tests stub the client by call order, so an extra call would move
 * every answer one place. Empty until a record has been fetched — a preview
 * with a relative address, for the length of a load.
 */
const WEBSITE_URL_KEY = ['websiteUrl'];

export const rememberWebsiteUrl = (client: QueryClient, url: string | undefined): void => {
  if (url) client.setQueryData(WEBSITE_URL_KEY, url);
};

export const useWebsiteUrl = (): string => {
  const { data } = useQuery({
    queryKey: WEBSITE_URL_KEY,
    queryFn: () => '',
    enabled: false,
    staleTime: Infinity,
  });
  return data ?? '';
};

/**
 * When this admin was opened. On the preview's address, so every visit to
 * an editor draws the picture afresh: the website keeps a drawing under its
 * address for an hour, and the address otherwise changes only when the
 * record does — a new design of the picture would sit behind the old one
 * for that hour, in the one place somebody is looking to check it.
 */
const OPENED_AT = Date.now();

/** Where the website draws the picture for `path`, as the previews ask for it. */
export const previewUrl = (website: string, path: string, updatedAt: string): string =>
  `${website}/og${path}?v=${Date.parse(updatedAt)}&preview=${OPENED_AT}`;
